import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { FilesService } from './files.service';
import { UIMessagingService } from './uimessaging.service';
import { DocViewerComponent } from './../components/ui/doc-viewer/doc-viewer.component';
import { VOBInfoWindowComponent } from './../components/vob-info-window/vob-info-window.component';
import firebase from 'firebase/app';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class PreviewDocumentService {
  constructor(
    private files_$: FilesService,
    private uiMessaging_$: UIMessagingService,
    private dialog_$: MatDialog,
  ) {}

  async validatedFileClick(filePath: string, fileName: string, auth_$: AuthService) {
    auth_$.showLoader('Opening the document viewer...', 'loading-document-viewer');
    await this.previewDocument(filePath, fileName, auth_$);
    auth_$.hideLoader();
  }

  getProperViewer(fileName: string) {
    const extension = fileName.split('.').pop().toLowerCase();
    let viewer = 'google';
    switch (extension) {
      case 'mp4':
      case 'avi':
        viewer = 'video';
        break;
      case 'pdf':
        viewer = 'google';
        break;
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
      case 'ppt':
      case 'pptx':
      case 'csv':
        viewer = 'office';
        break;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        viewer = 'url';
        break;
      default:
        viewer = 'google';
        break;
    }
    return viewer;
  }

  private handleError = (error?) => {
    console.trace('Error generating the disc download link.', error);
    const errorMessage = 'Error generating the disc download link. Please try again or contact support@nuagedx.com.';
    if (!error) {
      console.error(errorMessage);
    } else {
      console.error(errorMessage, error);
    }
    this.uiMessaging_$?.toastMessage(errorMessage, 'ERROR');
  };

  async handleDownloadFile(filePath, storageBucket, auth_$: AuthService) {
    auth_$.showLoader('Downloading the file...');
    const afterGetSignedURL = result => {
      console.log('result :', result);
      auth_$.hideLoader();
      window.open(result.data.url, '_blank');
    };
    return firebase
      .functions()
      .httpsCallable('firebaseUtilities-fileGetSignedURLV2')({ filePath, storageBucket })
      .then(result => afterGetSignedURL(result))
      .catch(err => this.handleError(err));
  }

  handleVOBFileClick(filePath, auth_$: AuthService) {
    const dialogRef = this.dialog_$.open(VOBInfoWindowComponent, { height: 'auto', width: 'auto' });
    dialogRef
      .afterClosed()
      .toPromise()
      .then(async res => {
        if (res === 'download') await this.handleDownloadFile(filePath, '', auth_$);
        else if (res === 'getConvertAndExport') this.handleConvertAndExport(firebase.storage().ref(), filePath);
      });
  }

  async handleConvertAndExport(pathReference: any, filePath: string) {
    pathReference
      .child(filePath)
      .getDownloadURL()
      .then(targetURL => {
        firebase
          .functions()
          .httpsCallable('cloudconvert-getConvertAndExport')({ url: targetURL })
          .then(result => console.log('result: ', result));
      });
  }

  async noZipFileClick(filePath: string, fileName: string, auth_$: AuthService) {
    // Validate file metadata.
    const validFileMetadata = await this.validateFileMeta(filePath, auth_$);
    if (!validFileMetadata) {
      console.log('The file metadata is not valid.');
    } else {
      this.validatedFileClick(filePath, fileName, auth_$);
    }
  }

  handleZIPFileClick(filePath: string, bucket = '', auth_$: AuthService) {
    return this.handleDownloadFile(filePath, bucket, auth_$);
  }

  async validateFileMeta(filePath: string, auth_$: AuthService) {
    const fileMetadata = (await firebase.functions().httpsCallable('firebaseUtilities-fileGetMetadata')({ filePath }))[
      'data'
    ]['url'];
    auth_$.hideLoader();

    if (!fileMetadata) {
      auth_$.hideLoader();
      this.uiMessaging_$.toastMessage(
        "It seems the file doesn't exist anymore. Please delete the file and import it again",
        'ERROR',
      );
      return false;
    }

    if (+fileMetadata['size'] === 0) {
      auth_$.hideLoader();
      this.uiMessaging_$.toastMessage(
        "It seems the file doesn't exist anymore. Please delete the file and import it again",
        'ERROR',
      );
      return false;
    }

    return true;
  }

  async previewDocument(filePath: string, fileName: string, auth_$: AuthService) {
    auth_$.showLoader('Loading the document ...');
    const fileUrl = await this.files_$.getFileUrl(filePath);

    if (!fileUrl) {
      this.uiMessaging_$.toastMessage('Error getting the file URL', 'ERROR');
    }

    this.dialog_$
      .open(DocViewerComponent, {
        width: '80%',
        data: {
          src: fileUrl.data.url,
          title: fileName,
          info: fileUrl.data.url,
          viewer: this.getProperViewer(fileName),
        },
      })
      .afterOpened()
      .subscribe(() => auth_$.hideLoader());
  }
}
